import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Input, Button, Empty, Spin, Alert, Tooltip } from 'antd';
import {
  SendOutlined, ReloadOutlined, MobileOutlined, CustomerServiceOutlined,
  CheckOutlined, HistoryOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n';
import mobileService from '../../../../services/mobileService';
import { can, HAB, formatDateHeure } from '../../../../variables/constants';

/**
 * Fil d'echange entre un gestionnaire et l'usager (victime / assure) sur un dossier.
 *
 * Servi par `/v1/mobile-backoffice/sinistres/{id}/messages`. Ce que l'agent ecrit ici
 * arrive dans l'application mobile de l'usager, et reciproquement - c'est le seul canal
 * de dialogue avec lui. Rien a voir avec le module `messagerie`, qui est un client IMAP
 * entre agents : melanger les deux ferait transiter un echange avec une victime par une
 * boite aux lettres externe, hors du dossier et hors controle d'acces.
 *
 * DEUX PROPRIETES DU BACKEND QUI CONTRAIGNENT CET ECRAN :
 *
 *  1. La lecture MARQUE COMME LUS les messages de l'usager, cote serveur, avant meme de
 *     renvoyer la page. Ce composant ne doit donc jamais etre monte pour alimenter un
 *     compteur de non-lus ni rafraichi par un intervalle : chaque appel eteint le signal
 *     « message non lu » sans que personne n'ait lu quoi que ce soit. Le rafraichissement
 *     reste manuel, et c'est deliberé.
 *  2. La page arrive du plus RECENT au plus ancien (tri backend impose). Un fil se lit
 *     dans l'autre sens : on inverse a l'affichage et les pages suivantes s'empilent
 *     VERS LE HAUT, comme « charger les messages plus anciens » dans une messagerie.
 *
 * `lu` decrit la lecture par l'AUTRE partie. Il n'est affiche que sur les messages
 * d'agent - ou il renseigne ce que l'agent veut savoir : l'usager a-t-il vu ma demande.
 * Sur un message d'usager il vaudrait toujours « lu » puisque l'ouverture de l'ecran
 * vient de le marquer ainsi : l'afficher serait du bruit.
 */

const { TextArea } = Input;

const MAX_CORPS = 4000; // Longueur exacte de message_dossier.corps. Au-dela : 400 backend.
const TAILLE_PAGE = 20;

const VERT = '#1a6b3c';

const Bulle = ({ message }) => {
  const { t } = useTranslation('dossierSinistre');
  const estAgent = message.auteurType === 'AGENT';
  return (
    <div style={{
      display: 'flex',
      justifyContent: estAgent ? 'flex-end' : 'flex-start',
      marginBottom: 12,
    }}>
      <div style={{ maxWidth: '76%', minWidth: 120 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3,
          justifyContent: estAgent ? 'flex-end' : 'flex-start',
          fontSize: 11, color: '#6b7280',
        }}>
          {estAgent
            ? <CustomerServiceOutlined style={{ color: VERT }} />
            : <MobileOutlined style={{ color: '#7c3aed' }} />}
          <span style={{ fontWeight: 650, color: '#374151' }}>
            {message.auteurLibelle || (estAgent ? t('filDiscussion.gestionnaire') : t('filDiscussion.usager'))}
          </span>
          <span>{formatDateHeure(message.dateEnvoi)}</span>
        </div>

        <div style={{
          background: estAgent ? '#eaf5ee' : '#f9fafb',
          border: `1px solid ${estAgent ? '#c7e3d1' : '#e5e7eb'}`,
          borderRadius: 12,
          borderTopRightRadius: estAgent ? 3 : 12,
          borderTopLeftRadius: estAgent ? 12 : 3,
          padding: '9px 12px',
          fontSize: 13,
          color: '#111827',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}>
          {message.corps}
        </div>

        {estAgent && (
          <div style={{ textAlign: 'right', fontSize: 10.5, color: '#9ca3af', marginTop: 3 }}>
            {message.lu
              ? (
                <Tooltip title={message.dateLecture ? t('filDiscussion.luLe', { date: formatDateHeure(message.dateLecture) }) : t('filDiscussion.luParUsager')}>
                  <span><CheckOutlined style={{ color: VERT }} /> {t('filDiscussion.luParUsager')}</span>
                </Tooltip>
              )
              : <span>{t('filDiscussion.nonLu')}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

export default function FilDiscussionMobile({
  sinistreTrackingId,
  hauteur = 420,
  numeroSinistre,
}) {
  const { t } = useTranslation('dossierSinistre');
  const [messages, setMessages] = useState([]); // du plus ancien au plus recent
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [chargement, setChargement] = useState(true);
  const [chargementAnciens, setChargementAnciens] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [corps, setCorps] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const zoneRef = useRef(null);

  const peutLire = can(HAB.MOBILE_MESSAGE_READ);
  const peutEcrire = can(HAB.MOBILE_MESSAGE_CREATE);

  const versLeBas = useCallback(() => {
    const zone = zoneRef.current;
    if (zone) zone.scrollTop = zone.scrollHeight;
  }, []);

  /** Premiere page : la plus recente. On l'inverse pour lire le fil dans l'ordre. */
  const charger = useCallback(async () => {
    if (!sinistreTrackingId || !peutLire) return;
    setChargement(true);
    setErreur(null);
    try {
      const res = await mobileService.messages(sinistreTrackingId, { page: 1, size: TAILLE_PAGE });
      setMessages([...res.items].reverse());
      setTotal(res.total);
      setPage(1);
    } catch (err) {
      setErreur(err?.response?.data?.message || i18n.t('dossierSinistre:filDiscussion.chargementImpossible'));
      setMessages([]);
    } finally {
      setChargement(false);
    }
  }, [sinistreTrackingId, peutLire]);

  useEffect(() => { charger(); }, [charger]);

  // Apres le premier chargement seulement : le fil doit s'ouvrir sur le dernier message.
  useEffect(() => { if (!chargement) versLeBas(); }, [chargement, versLeBas]);

  const chargerAnciens = async () => {
    setChargementAnciens(true);
    try {
      const suivante = page + 1;
      const res = await mobileService.messages(sinistreTrackingId, { page: suivante, size: TAILLE_PAGE });
      // Empilement vers le haut : on conserve la position de lecture en compensant
      // l'accroissement de hauteur, sinon l'agent est ejecte du passage qu'il lisait.
      const zone = zoneRef.current;
      const hauteurAvant = zone ? zone.scrollHeight : 0;
      setMessages((precedents) => [...[...res.items].reverse(), ...precedents]);
      setTotal(res.total);
      setPage(suivante);
      requestAnimationFrame(() => {
        if (zone) zone.scrollTop = zone.scrollHeight - hauteurAvant;
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || t('filDiscussion.anciensImpossible'));
    } finally {
      setChargementAnciens(false);
    }
  };

  const envoyer = async () => {
    const texte = corps.trim();
    // Le backend rejette un corps vide en 400 : on s'arrete avant l'aller-retour.
    if (!texte) return;
    setEnvoi(true);
    try {
      const cree = await mobileService.repondre(sinistreTrackingId, { corps: texte });
      // On ajoute la reponse renvoyee par le serveur plutot que de tout recharger :
      // un rechargement remarquerait comme lus d'eventuels messages arrives entre-temps
      // et les ferait disparaitre du signal de non-lu sans avoir ete vus.
      setMessages((precedents) => (cree ? [...precedents, cree] : precedents));
      setTotal((t) => t + 1);
      setCorps('');
      toast.success(t('filDiscussion.messageEnvoye'));
      requestAnimationFrame(versLeBas);
    } catch (err) {
      toast.error(err?.response?.data?.message || t('filDiscussion.envoiImpossible'));
    } finally {
      setEnvoi(false);
    }
  };

  // Ctrl/Cmd + Entree envoie ; Entree seul saute une ligne. Un message part vers une
  // victime et n'est pas rattrapable : l'envoi ne doit pas etre a une touche d'ecart.
  const surTouche = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (!envoi && corps.trim() && peutEcrire) envoyer();
    }
  };

  if (!peutLire) {
    return (
      <Alert
        type="info"
        showIcon
        message={t('filDiscussion.lectureReservee')}
      />
    );
  }

  const restants = Math.max(total - messages.length, 0);

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 10, gap: 8, flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 11.5, color: '#6b7280' }}>
          {numeroSinistre
            ? t('filDiscussion.avertissementDossier', { numero: numeroSinistre })
            : t('filDiscussion.avertissement')}
        </span>
        <Button size="small" type="text" icon={<ReloadOutlined />} onClick={charger} loading={chargement}>
          {t('filDiscussion.actualiser')}
        </Button>
      </div>

      {erreur && <Alert type="error" showIcon message={erreur} style={{ marginBottom: 12 }} />}

      <div
        ref={zoneRef}
        style={{
          height: hauteur,
          overflowY: 'auto',
          border: '1px solid #e5e7eb',
          borderRadius: 10,
          background: '#fff',
          padding: 14,
        }}
      >
        {chargement && (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        )}

        {!chargement && !messages.length && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('filDiscussion.aucunEchange')}
            style={{ marginTop: hauteur / 4 }}
          />
        )}

        {!chargement && restants > 0 && (
          <div style={{ textAlign: 'center', marginBottom: 12 }}>
            <Button
              size="small"
              icon={<HistoryOutlined />}
              loading={chargementAnciens}
              onClick={chargerAnciens}
            >
              {t('filDiscussion.messagesAnciens', { n: restants })}
            </Button>
          </div>
        )}

        {!chargement && messages.map((m) => (
          <Bulle key={m.messageTrackingId} message={m} />
        ))}
      </div>

      <div style={{ marginTop: 10 }}>
        <TextArea
          rows={3}
          value={corps}
          onChange={(e) => setCorps(e.target.value)}
          onKeyDown={surTouche}
          maxLength={MAX_CORPS}
          showCount
          disabled={!peutEcrire || envoi}
          placeholder={peutEcrire
            ? t('filDiscussion.placeholder')
            : t('filDiscussion.ecritureReservee')}
        />
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 8, gap: 8,
        }}>
          <span style={{ fontSize: 11, color: '#9ca3af' }}>
            {t('filDiscussion.aide')}
          </span>
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={envoi}
            disabled={!peutEcrire || !corps.trim()}
            onClick={envoyer}
            // Style conditionnel : le vert force en ligne survivrait a l'etat desactive
            // et donnerait un bouton d'apparence active qui ne repond pas.
            style={!peutEcrire || !corps.trim() ? undefined : { background: VERT, borderColor: VERT }}
          >
            {t('filDiscussion.envoyer')}
          </Button>
        </div>
      </div>
    </div>
  );
}
